import { Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@xxtract/ui';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { useAttentionProducts } from '../hooks/useDashboard';

interface AttentionListProps {
  gln: string;
}

export function AttentionList({ gln }: AttentionListProps) {
  const { t } = useTranslation();
  const attention = useAttentionProducts(gln);

  if (attention.isError) {
    return (
      <Card data-testid="attention-unavailable" role="status">
        <CardHeader>
          <CardTitle>{t('dashboard.attention.unavailableTitle')}</CardTitle>
          <CardDescription>{t('dashboard.attention.unavailableDescription')}</CardDescription>
        </CardHeader>
        <CardContent>
          <Button className="min-h-11" onClick={() => void attention.refetch()} variant="secondary">{t('common.retry')}</Button>
        </CardContent>
      </Card>
    );
  }

  if (attention.isLoading) {
    return <div className="skeleton h-56 rounded-[var(--radius-card)]" data-testid="attention-loading" />;
  }

  const products = attention.data?.data ?? [];

  return (
    <Card data-testid="attention-list">
      <CardHeader>
        <CardTitle>{t('dashboard.attention.title')}</CardTitle>
        <CardDescription>{t('dashboard.attention.description')}</CardDescription>
      </CardHeader>
      <CardContent>
        {products.length === 0 ? (
          <p className="text-sm text-[var(--foreground-muted)]">{t('dashboard.attention.empty')}</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('products.productName')}</TableHead>
                <TableHead>{t('dashboard.attention.missing')}</TableHead>
                <TableHead>{t('products.score')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {products.map((product) => (
                <TableRow key={product.id}>
                  <TableCell>
                    <Link className="font-medium text-[var(--secondary)] underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--secondary)]" to={`/products?attention=1${product.brandId === null ? '' : `&brandId=${encodeURIComponent(product.brandId)}`}`}> 
                      {product.name}
                    </Link>
                    <span className="block text-xs text-[var(--foreground-muted)]">{product.gtin}</span>
                  </TableCell>
                  <TableCell>{t('dashboard.attention.missingCount', { count: product.missingRequiredFieldCount })}</TableCell>
                  <TableCell>{t('dashboard.attention.score', { score: product.score })}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
